import jsPDF from 'jspdf';
import moment from 'moment';
import { useEffect } from 'react';
import { resolveScheduleDateRange } from '../../utils/utils';

interface StaffScheduleReportPdfReportPDFProps {
    data: any;
    setPdfBlob: (blob: Blob) => void;
    formData?: any;
}

const StaffScheduleReportPdf: React.FC<StaffScheduleReportPdfReportPDFProps> = ({ data, setPdfBlob, formData }) => {
    // Group and sort the data by DIST_NAM and SiteName
    const groupByDistrictAndSiteType = (data: any[]) => {
        // First, group all staff by SiteID to ensure all staff for the same site are together
        const sitesBySiteId: { [key: string]: any[] } = {};
        
        data.forEach(item => {
            const siteId = item.SiteID?.toString();
            if (!siteId) return;
            
            if (!sitesBySiteId[siteId]) {
                sitesBySiteId[siteId] = [];
            }
            sitesBySiteId[siteId].push(item);
        });

        // Now process each site group
        return Object.keys(sitesBySiteId).reduce((acc: any, siteId: string) => {
            const siteStaff = sitesBySiteId[siteId];
            if (siteStaff.length === 0) return acc;
            
            // Use the first staff member to get site information
            const firstStaff = siteStaff[0];
            const districtKey = firstStaff.DIST_NAM?.toLowerCase().trim().replace(/\s+/g, ' ');
            const siteType = getSiteTypeLabel(firstStaff.SiteType);
            
            // Initialize district if needed
            if (!acc[districtKey]) {
                acc[districtKey] = {
                    originalName: firstStaff.DIST_NAM,
                    data: {}
                };
            }
            
            // Initialize site type if needed
            if (!acc[districtKey].data[siteType]) {
                acc[districtKey].data[siteType] = {
                    originalName: siteType,
                    data: {}
                };
            }
            
            // Initialize site with SiteID as key
            if (!acc[districtKey].data[siteType].data[siteId]) {
                acc[districtKey].data[siteType].data[siteId] = {
                    originalName: firstStaff.SiteName,
                    siteId: firstStaff.SiteID,
                    staff: []
                };
            }
            
            // Process all staff for this site
            siteStaff.forEach(item => {
                // Look for an existing staff member with the same PersonalID and SitePos
                const existingStaff = acc[districtKey].data[siteType].data[siteId].staff.find(
                    (s: any) => {
                        if (s.PersonalID !== item.PersonalID || s.SitePos !== item.SitePos) return false;
                        // A true conflict = both have a value for the same day BUT the values are different
                        const hasConflict =
                            (s.MON_B && item.MON_B && s.MON_B !== item.MON_B) ||
                            (s.TUE_B && item.TUE_B && s.TUE_B !== item.TUE_B) ||
                            (s.WED_B && item.WED_B && s.WED_B !== item.WED_B) ||
                            (s.THU_B && item.THU_B && s.THU_B !== item.THU_B) ||
                            (s.FRI_B && item.FRI_B && s.FRI_B !== item.FRI_B);
                        return !hasConflict; // Only merge if no real conflict
                    }
                );

                if (existingStaff) {
                    // Safe to merge - fill in NULL day slots with the incoming item's values
                    if (item.MON_B) { existingStaff.MON_B = item.MON_B; existingStaff.MON_E = item.MON_E; }
                    if (item.TUE_B) { existingStaff.TUE_B = item.TUE_B; existingStaff.TUE_E = item.TUE_E; }
                    if (item.WED_B) { existingStaff.WED_B = item.WED_B; existingStaff.WED_E = item.WED_E; }
                    if (item.THU_B) { existingStaff.THU_B = item.THU_B; existingStaff.THU_E = item.THU_E; }
                    if (item.FRI_B) { existingStaff.FRI_B = item.FRI_B; existingStaff.FRI_E = item.FRI_E; }
                } else {
                    // Different schedule period or conflict detected - show as a separate row
                    acc[districtKey].data[siteType].data[siteId].staff.push({ ...item });
                }
            });

            return acc;
        }, {});
    };


    const SITE_TYPE_ORDER: any = {
        "Before School Programs": 1,
        "After School Programs": 2,
        "During School Programs": 3,
        "Unknown Site Type": 99
    };

    const getSiteTypeLabel = (siteType: any) => {
        switch (siteType) {
            case 1: return "Before School Programs";
            case 2: return "During School Programs";
            case 3: return "After School Programs";
            default: return "Unknown Site Type";
        }
    };

    const generatePDF = () => {
        const doc = new jsPDF('portrait', 'mm', 'a4'); // A4 portrait

        const pageWidth = 210; // Total width for A4 portrait
        const pageHeight = 297; // Total height for A4 portrait
        const margin = 10; // Margin on both sides
        const usableWidth = pageWidth - margin * 2; // Usable width after subtracting margins
        const lineHeight = 4; // Line height
        const footerHeight = 15; // Space reserved for footer (date and page number)
        const fontSize = 8; // Font size for content
        let yPos = 10;  // Initial Y position
        const todayDate = moment().format('MM-DD-YYYY');

        // Helper function to format time: keep times <= 12:00 as-is, convert times > 12:00 to 12-hour format
        const formatTime = (timeStr: string | null | undefined): string => {
            if (!timeStr) return '';
            const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
            if (!timeMatch) return timeStr; // Return as-is if format doesn't match

            const hours = parseInt(timeMatch[1], 10);
            const minutes = timeMatch[2];

            if (hours > 12) {
                const formattedHours = (hours - 12).toString().padStart(2, '0');
                return `${formattedHours}:${minutes}`;
            }
            return timeStr; // Keep as-is for times <= 12:00
        };

        // Title
        doc.setFontSize(10); // Reduced title font size

        const range = resolveScheduleDateRange(formData?.StartDate, formData?.EndDate);
        const startDate = moment(range.startDate).format('MM-DD-YYYY');
        const endDate = moment(range.endDate).format('MM-DD-YYYY');

        const title = startDate === endDate
            ? `Staff Schedule (${startDate})`
            : `Staff Schedule (${startDate} - ${endDate})`;
        doc.text(title, margin, yPos);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos + 2, pageWidth - margin, yPos + 2);  // Horizontal line under the title
        yPos += lineHeight * 1.5;

        // Group and sort the data
        const groupedData = groupByDistrictAndSiteType(data);

        // Sort by DIST_NAM
        const sortedDistrictKeys = Object.keys(groupedData).sort((a, b) => a.localeCompare(b));

        // Loop through sorted districts
        sortedDistrictKeys.forEach((district: string) => {
            // Print District Name (1st level)
            if (yPos > pageHeight - margin - footerHeight) {  // Reserve space for the footer
                doc.addPage();
                yPos = margin + lineHeight;  // Reset Y position on a new page
            }
            doc.setFontSize(fontSize);
            doc.setFont('helvetica', 'bold');
            doc.text(groupedData[district].originalName, margin, yPos); // Use original district name
            yPos += lineHeight * 1.5; // Add extra space after district name

            // const siteTypes = Object.keys(groupedData[district].data);
            const siteTypes = Object.keys(groupedData[district].data).sort((a: string, b: string) => {
                const orderA = SITE_TYPE_ORDER[a] || 99;
                const orderB = SITE_TYPE_ORDER[b] || 99;
                return orderA - orderB;
            });
            // Loop through site types for the district
            siteTypes.forEach((siteType: string) => {
                if (yPos > pageHeight - margin - footerHeight) {  // Reserve space for the footer
                    doc.addPage();
                    yPos = margin + lineHeight;  // Reset Y position on a new page
                }
                yPos += lineHeight;
                const siteTypeMargin = margin + 2; // Indent for Site Type
                doc.setFont('helvetica', 'bold');
                doc.text(groupedData[district].data[siteType].originalName, siteTypeMargin, yPos); // Use original site type name
                yPos += lineHeight * 1.2; // Add extra space after site type name

                const siteIds = Object.keys(groupedData[district].data[siteType].data).sort((a: string, b: string) => {
                    const siteA = groupedData[district].data[siteType].data[a];
                    const siteB = groupedData[district].data[siteType].data[b];
                    return (siteA.originalName || '').localeCompare(siteB.originalName || '');
                });
                // Loop through site IDs under the site type
                siteIds.forEach((siteIdKey: string) => {
                    if (yPos > pageHeight - margin - footerHeight) {  // Reserve space for the footer
                        doc.addPage();
                        yPos = margin + lineHeight;  // Reset Y position on a new page
                    }
                    yPos += lineHeight;
                    doc.setFont('helvetica', 'bold');
                    const siteNameMargin = siteTypeMargin + 2; // Further indent for Site Name
                    doc.text(groupedData[district].data[siteType].data[siteIdKey].originalName, siteNameMargin, yPos); // Use original site name
                    yPos += lineHeight * 1.5; // Add extra space after site name

                    // Print headers for the weekdays
                    doc.setFont('helvetica', 'normal');
                    const headers = [
                        // { text: 'Phone', x: margin + usableWidth * 0.40 },
                        { text: 'Monday', x: margin + usableWidth * 0.40 },
                        { text: 'Tuesday', x: margin + usableWidth * 0.52 },
                        { text: 'Wednesday', x: margin + usableWidth * 0.64 },
                        { text: 'Thursday', x: margin + usableWidth * 0.76 },
                        { text: 'Friday', x: margin + usableWidth * 0.88 }
                    ];

                    headers.forEach((header) => {
                        doc.text(header.text, header.x, yPos);
                    });
                    yPos += lineHeight * 1.5; // Add extra space after headers

                    // Loop through staff data for the site
                    const staffList = groupedData[district].data[siteType].data[siteIdKey].staff
                        .sort((a: { FirstName: any }, b: { FirstName: any }) => {
                            const firstNameA = (a.FirstName || '').toString().toLowerCase();
                            const firstNameB = (b.FirstName || '').toString().toLowerCase();
                            return firstNameA.localeCompare(firstNameB);
                        });

                    staffList.forEach((staff: { FirstName: any; LastName: any; SitePos: any; OTHERPHONE: any; MON_B: any; MON_E: any; TUE_B: any; TUE_E: any; WED_B: any; WED_E: any; THU_B: any; THU_E: any; FRI_B: any; FRI_E: any; }, index: number) => {
                        const { FirstName, LastName, SitePos, OTHERPHONE, MON_B, MON_E, TUE_B, TUE_E, WED_B, WED_E, THU_B, THU_E, FRI_B, FRI_E } = staff;

                        // Print Staff Data, aligned with headers
                        const fullName = `${FirstName},${LastName}`;
                        const nameMaxLength = 16; // Maximum characters before wrapping
                        let nameHeight = lineHeight; // Default height for single line

                        // Add top margin before every staff entry
                        yPos += lineHeight * 0.6; // Increased by 25% (0.45 * 1.25)

                        // Check if name needs to be split
                        if (fullName.length > nameMaxLength) {
                            // Smart word-aware wrapping
                            let remainingText = fullName;
                            let nameYPos = yPos;
                            let linesUsed = 0;

                            while (remainingText.length > nameMaxLength) {
                                // Find the best break point within the limit
                                let breakPoint = nameMaxLength;

                                // Try to break at space, comma, or hyphen
                                for (let i = nameMaxLength - 1; i >= Math.max(0, nameMaxLength - 10); i--) {
                                    const char = remainingText[i];
                                    if (char === ' ' || char === ',' || char === '-') {
                                        breakPoint = i + 1;
                                        break;
                                    }
                                }

                                // Take the chunk up to break point
                                const chunk = remainingText.substring(0, breakPoint);
                                doc.text(chunk, siteNameMargin, nameYPos);
                                remainingText = remainingText.substring(breakPoint);
                                nameYPos += lineHeight;
                                linesUsed++;
                            }

                            // Print remaining text
                            if (remainingText.trim()) {
                                doc.text(remainingText.trim(), siteNameMargin, nameYPos);
                                linesUsed++;
                            }

                            // Adjust Y position for other data to align with the last line of name
                            yPos += (linesUsed * lineHeight) - lineHeight;
                        } else {
                            // Print name on single line
                            doc.text(fullName, siteNameMargin, yPos);
                        }

                        // Print other data at the adjusted Y position
                        doc.text(SitePos, margin + usableWidth * 0.20, yPos); // Align with Site Position
                        doc.text(`${formatTime(MON_B)} - ${formatTime(MON_E)}`, margin + usableWidth * 0.40, yPos); // Align with Monday
                        doc.text(`${formatTime(TUE_B)} - ${formatTime(TUE_E)}`, margin + usableWidth * 0.52, yPos); // Align with Tuesday
                        doc.text(`${formatTime(WED_B)} - ${formatTime(WED_E)}`, margin + usableWidth * 0.64, yPos); // Align with Wednesday
                        doc.text(`${formatTime(THU_B)} - ${formatTime(THU_E)}`, margin + usableWidth * 0.76, yPos); // Align with Thursday
                        doc.text(`${formatTime(FRI_B)} - ${formatTime(FRI_E)}`, margin + usableWidth * 0.88, yPos); // Align with Friday

                        yPos += lineHeight * 0.35;

                        // Divider strictly BETWEEN rows: add padding, draw line, add padding
                        if (index < staffList.length - 1) {
                            const dividerPadding = lineHeight * 0.195;

                            if (yPos > pageHeight - margin - footerHeight) {
                                doc.addPage();
                                yPos = margin + lineHeight;
                            }

                            doc.setDrawColor(0, 0, 0);
                            doc.setLineWidth(0.35);
                            doc.line(siteNameMargin, yPos, pageWidth - margin - 5, yPos);

                            yPos += dividerPadding;
                        } else {
                            // Add bottom margin for last entry
                            // yPos += lineHeight * 0.45;
                        }

                        // Page break if Y position is too close to the bottom (leaving space for the footer)
                        if (yPos > pageHeight - margin - footerHeight) {  // Reserve space for the footer
                            doc.addPage();
                            yPos = margin + lineHeight;  // Reset Y position on a new page
                        }
                    });

                    yPos += lineHeight;
                });
            });
        });

        // Add date and page number at the bottom
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(fontSize);
            doc.text(todayDate, pageWidth - margin - 40, pageHeight - margin - 2); // Date at bottom right
            doc.text(`Page ${i} of ${pageCount}`, margin, pageHeight - margin - 2); // Page number at bottom left
        }

        const pdfBlob = doc.output('blob');
        setPdfBlob(pdfBlob);
    };


    // const generatePDF = () => {
    //     const doc = new jsPDF('portrait', 'mm', 'a4'); // A4 portrait

    //     const pageWidth = 210; // Total width for A4 portrait
    //     const pageHeight = 297; // Total height for A4 portrait
    //     const margin = 10; // Margin on both sides
    //     const usableWidth = pageWidth - margin * 2; // Usable width after subtracting margins
    //     const lineHeight = 4; // Reduced line height to fit more lines
    //     const fontSize = 8; // Reduced font size for more content
    //     let yPos = 10;  // Initial Y position
    //     const todayDate = moment().format('DD-MM-YYYY');

    //     // Title
    //     doc.setFontSize(10); // Reduced title font size
    //     doc.text('Staff Schedule', margin, yPos);
    //     doc.setLineWidth(0.5);
    //     doc.line(margin, yPos + 2, pageWidth - margin, yPos + 2);  // Horizontal line under the title
    //     yPos += lineHeight * 1.5;

    //     // Group and sort the data
    //     const groupedData = groupByDistrictAndSiteType(data);

    //     // Sort by DIST_NAM
    //     const sortedDistrictKeys = Object.keys(groupedData).sort((a, b) => a.localeCompare(b));

    //     // Loop through sorted districts
    //     sortedDistrictKeys.forEach((district) => {
    //         // Print District Name (1st level)
    //         doc.setFontSize(fontSize);
    //         doc.setFont('helvetica', 'bold');
    //         doc.text(district, margin, yPos); // No indentation for DIST_NAM
    //         // yPos += lineHeight;

    //         const siteTypes = Object.keys(groupedData[district]);

    //         // Loop through site types for the district
    //         siteTypes.forEach((siteType) => {
    //             // Print Site Type (2nd level, indented)
    //             yPos += lineHeight;
    //             // doc.setFont('helvetica', 'normal');
    //             const siteTypeMargin = margin + 2; // Indent for Site Type
    //             doc.setFont('helvetica', 'bold');
    //             doc.text(siteType, siteTypeMargin, yPos); // Indented for site type
    //             yPos += lineHeight;

    //             const siteNames = Object.keys(groupedData[district][siteType]);

    //             // Loop through site names under the site type
    //             siteNames.forEach((siteName) => {
    //                 yPos += lineHeight;
    //                 doc.setFont('helvetica', 'bold');
    //                 const siteNameMargin = siteTypeMargin + 2; // Further indent for Site Name
    //                 doc.text(siteName, siteNameMargin, yPos); // Indented for site name

    //                 // Print headers for the weekdays
    //                 doc.setFont('helvetica', 'normal');
    //                 const headers = [
    //                     { text: 'Monday', x: margin + usableWidth * 0.42 },
    //                     { text: 'Tuesday', x: margin + usableWidth * 0.54 },
    //                     { text: 'Wednesday', x: margin + usableWidth * 0.66 },
    //                     { text: 'Thursday', x: margin + usableWidth * 0.78 },
    //                     { text: 'Friday', x: margin + usableWidth * 0.90 }
    //                 ];

    //                 headers.forEach((header) => {
    //                     doc.text(header.text, header.x, yPos);
    //                 });
    //                 yPos += lineHeight;

    //                 // Loop through staff data for the site
    //                 groupedData[district][siteType][siteName].forEach((staff: { FirstName: any; LastName: any; SitePos: any; MON_B: any; MON_E: any; TUE_B: any; TUE_E: any; WED_B: any; WED_E: any; THU_B: any; THU_E: any; FRI_B: any; FRI_E: any; }) => {
    //                     const { FirstName, LastName, SitePos, MON_B, MON_E, TUE_B, TUE_E, WED_B, WED_E, THU_B, THU_E, FRI_B, FRI_E } = staff;

    //                     // Print Staff Data, aligned with the headers
    //                     doc.text(`${FirstName} ${LastName}`, siteNameMargin, yPos); // Indent with site name
    //                     doc.text(SitePos, margin + usableWidth * 0.25, yPos); // Align with Site Position
    //                     doc.text(`${MON_B || ''} - ${MON_E || ''}`, margin + usableWidth * 0.42, yPos); // Align with Monday
    //                     doc.text(`${TUE_B || ''} - ${TUE_E || ''}`, margin + usableWidth * 0.54, yPos); // Align with Tuesday
    //                     doc.text(`${WED_B || ''} - ${WED_E || ''}`, margin + usableWidth * 0.66, yPos); // Align with Wednesday
    //                     doc.text(`${THU_B || ''} - ${THU_E || ''}`, margin + usableWidth * 0.78, yPos); // Align with Thursday
    //                     doc.text(`${FRI_B || ''} - ${FRI_E || ''}`, margin + usableWidth * 0.90, yPos); // Align with Friday

    //                     yPos += lineHeight;

    //                     // Page break if Y position is too close to the bottom
    //                     if (yPos > pageHeight - margin * 2) {
    //                         doc.addPage();
    //                         yPos = margin + lineHeight;  // Reset Y position on a new page
    //                     }
    //                 });
    //                 yPos += lineHeight;
    //             });
    //         });
    //     });

    //     // Add date and page number at the bottom
    //     const pageCount = doc.getNumberOfPages();
    //     for (let i = 1; i <= pageCount; i++) {
    //         doc.setPage(i);
    //         doc.setFontSize(fontSize);
    //         doc.text(todayDate, pageWidth - margin - 40, pageHeight - margin); // Date at bottom right
    //         doc.text(`Page ${i} of ${pageCount}`, margin, pageHeight - margin); // Page number at bottom left
    //     }

    //     const pdfBlob = doc.output('blob');
    //     setPdfBlob(pdfBlob);
    // };


    useEffect(() => {
        if (data) {
            generatePDF();
        }
    }, [data]);

    return null; // This component doesn't render anything, it just generates the PDF
};

export default StaffScheduleReportPdf;
